<template>
  <section class="result-lesson-card">
    <v-card class="mb-3">
      <v-row class="mx-3 pt-3">
        <v-col cols="6" class="ov">
          <h2>{{ lesson.name }}</h2>
          <span>
            Average score on pre-quiz across {{ totalCompleted }}
            {{ totalCompleted === 1 ? 'attempt' : 'attempts' }}:
            <strong>{{ `${average.prequiz}/${preQuiz.length}` }}</strong>
          </span>
          <span>
            Average score on post-quiz across {{ totalCompleted }}
            {{ totalCompleted === 1 ? 'attempt' : 'attempts' }}:
            <strong>{{ `${average.postquiz}/${postQuiz.length}` }}</strong>
          </span>
          <span>
            {{
              `Improvement across all ${totalCompleted} attempt${
                totalCompleted > 1 ? 's' : ''
              }`
            }}
            <strong>{{ improvement }}</strong>
          </span>
        </v-col>
        <v-col cols="4" offse="2" class="ov">
          <v-select
            :items="items"
            v-model="selected"
            :label="
              `Last Attempt / Completed ${this.capitalizeFirstLetter(
                this.lessonTerm
              )}`
            "
          ></v-select>
          <span>
            Number of attempted {{ lessonTerm }}s:
            <strong>{{ items.length }}</strong>
          </span>
          <span>
            Current {{ lessonTerm }} progress:
            <strong> {{ progress }}</strong>
          </span>
        </v-col>
        <v-col cols="12" class="ov mt-3">
          <v-card-actions>
            <v-spacer />
            <v-btn
              v-if="preQuiz.length"
              @click="lessonTileClick('pre-quiz')"
              :class="{
                activeButton: active == 'pre-quiz'
              }"
              :dark="active == 'pre-quiz' ? true : false"
              >pre quiz</v-btn
            >
            <v-btn
              v-if="otherQuestions.length"
              @click="lessonTileClick('lesson')"
              :class="{
                activeButton: active == 'lesson'
              }"
              :dark="active == 'lesson' ? true : false"
              >{{ lessonTerm }}</v-btn
            >
            <v-btn
              v-if="postQuiz.length"
              @click="lessonTileClick('post-quiz')"
              :class="{
                activeButton: active == 'post-quiz'
              }"
              :dark="active == 'post-quiz' ? true : false"
              >post quiz</v-btn
            >
          </v-card-actions>
        </v-col>
      </v-row>
      <div
        :style="{
          position: 'relative',
          backgroundColor: 'var(--v-primary-base)',
          height: '25px'
        }"
      ></div>
      <v-slide-y-transition color="#000000">
        <div class="border-top tab-wrap pa-3 pb-0" v-if="active == 'pre-quiz'">
          <quiz-result
            v-for="(quiz, index) in preQuiz"
            :key="index"
            :quiz="quiz"
            :questionIndex="index"
            userType="teacher"
            class="mb-3"
          />
        </div>
        <div
          class="border-top tab-wrap pa-3 pb-0"
          v-else-if="active == 'lesson'"
        >
          <view-lesson :questions="otherQuestions" />
        </div>
        <div
          class="border-top tab-wrap pa-3 pb-0"
          v-else-if="active == 'post-quiz'"
        >
          <quiz-result
            v-for="(quiz, index) in postQuiz"
            :key="index"
            :quiz="quiz"
            :questionIndex="index"
            userType="teacher"
            class="mb-3"
          />
        </div>
      </v-slide-y-transition>
    </v-card>
  </section>
</template>
<script>
import _ from 'lodash'
import { mapState } from 'vuex'
import Lesson from '@/modules/lesson/models/Lesson'
import QuizResult from '@/modules/student/views/classes/results/QuizResult'
import ViewLesson from '@/modules/student/views/classes/results/ViewLesson'

export default {
  name: 'ResultLessonCard',
  components: {
    QuizResult,
    ViewLesson
  },
  props: {
    studentLessons: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      selected: null,
      active: null,
      userLesson: null
    }
  },
  watch: {},
  computed: {
    ...mapState(['lessonTerm']),
    index() {
      this.setDefaultComplete()
      return this.studentLessons.findIndex(x => x.updated_at === this.selected)
    },
    items() {
      let items = []
      _.forEach(this.studentLessons, value => {
        items = [
          ...items,
          {
            text: this.itemDateText(value),
            value: value.updated_at
          }
        ]
      })
      return items
    },
    lesson() {
      let lesson = Lesson.find(this.studentLessons[0].lesson_id)
      if (!lesson) return []
      return lesson
    },
    progress() {
      if (this.studentLessons[this.index].is_completed) return '100%'
      let current = this.studentLessons[this.index].current_slide
      let lesson = Lesson.find(this.studentLessons[this.index].lesson_id)

      let total = lesson.total_slides
      return `${Math.floor((current / total) * 100)}%`
    },
    improvement() {
      if (this.totalCompleted <= 1)
        return 'Student must retake to see improvement'
      let pre = this.studentLessons[this.index].pre_quiz
      let post = this.studentLessons[this.index].post_quiz
      let total = this.postQuiz.length
      let percentage = Math.floor(((post - pre) / total) * 100)
      return `${percentage < 0 ? 0 : percentage}% improvement`
    },
    preQuiz() {
      if (!this.studentLessons[this.index]) return []
      if (!this.studentLessons[this.index].user_lesson_slides) return []
      return this.studentLessons[this.index].user_lesson_slides
        .filter(slide => slide.lesson_slide.name === 'pre_quiz')
        .sort((a, b) => a.lesson_slide.order - b.lesson_slide.order)
    },
    postQuiz() {
      if (!this.studentLessons[this.index]) return []
      if (!this.studentLessons[this.index].user_lesson_slides) return []
      return this.studentLessons[this.index].user_lesson_slides
        .filter(slide => slide.lesson_slide.name === 'post_quiz')
        .sort((a, b) => a.lesson_slide.order - b.lesson_slide.order)
    },
    otherQuestions() {
      if (!this.studentLessons[this.index]) return []
      if (!this.studentLessons[this.index].user_lesson_slides) return []
      return this.studentLessons[this.index].user_lesson_slides
        .filter(
          slide =>
            slide.lesson_slide.name !== 'post_quiz' &&
            slide.lesson_slide.name !== 'pre_quiz' &&
            slide.lesson_slide.name !== 'static'
        )
        .sort((a, b) => a.lesson_slide.order - b.lesson_slide.order)
    },
    totalCompleted() {
      return this.studentLessons.filter(val => val.completed_at).length
    },
    average() {
      let prequiz = 0,
        postquiz = 0
      _.forEach(this.studentLessons, lesson => {
        prequiz += parseInt(lesson.pre_quiz)
        postquiz += parseInt(lesson.post_quiz)
      })
      prequiz = this.totalCompleted > 0 ? prequiz / this.totalCompleted : 0
      postquiz = this.totalCompleted > 0 ? postquiz / this.totalCompleted : 0
      return { prequiz, postquiz }
    }
  },
  async mounted() {},
  methods: {
    lessonTileClick(type) {
      if (this.active == type) {
        this.active = null
      } else {
        this.active = type
      }
      this.$emit('expand')
    },
    setDefaultComplete() {
      if (this.items.length && this.selected === null) {
        this.selected = this.items[0].value
      }
    },
    itemDateText({ updated_at, is_completed }) {
      if (is_completed) {
        return `Completed on ${this.$moment(updated_at).format('Do MMMM YYYY')}`
      } else {
        return `In progress. Last attempted on ${this.$moment(
          updated_at
        ).format('Do MMMM YYYY')}`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.result-lesson-card {
  overflow: hidden;
  .ov {
    span {
      display: block;
    }
  }

  h2 {
    color: var(--v-primary-base);
  }

  .border-top {
    border-bottom: solid 1px #ccc;
    border-left: solid 1px #ccc;
    border-right: solid 1px #ccc;
  }

  .activeButton {
    background: var(--v-primary-base) !important;
    box-shadow: none !important;
    height: 65px;
    margin-bottom: -26px;
  }
}
</style>
